import React, { ReactNode } from "react";
import {
  Paper,
  Box,
  PaperProps,
  useTheme,
  alpha,
  Typography,
  Tooltip,
  Button,
  IconButton,
  lighten,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import { FiInfo } from "react-icons/fi";

// Keyframes para animações
const pulseAnimation = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.01);
  }
`;

const gridMoveAnimation = keyframes`
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 40px 40px;
  }
`;

const glowAnimation = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(42, 244, 151, 0.4);
  }
  50% {
    box-shadow: 0 0 30px rgba(42, 244, 151, 0.7);
  }
`;

// Ver documentação completa no arquivo index.d.ts
export interface NeonCardProps extends Omit<PaperProps, "variant" | "title"> {
  /** Conteúdo do card */
  children: ReactNode;
  /** Cor do efeito neon (RGBA/HEX). Padrão: theme.palette.primary.main */
  neonColor?: string;
  /** Opacidade da grade de fundo (0-1). Padrão: 0.05 */
  gridOpacity?: number;
  /** Estilo da borda. Padrão: 'outlined' */
  variant?: "outlined" | "elevation";
  /** Intensidade do brilho: 'low', 'medium', 'high'. Padrão: 'medium' */
  neonGlowIntensity?: "low" | "medium" | "high";
  /** Background personalizado. Ex: 'linear-gradient(...)' */
  customBackground?: string;
  /** Titulo do Card */
  cardTitle?: string | JSX.Element;
  /** Tooltip que fica do lado do card, só funciona se tiver um cardTitle */
  tooltipInfo?: string;
}

const NeonCard = ({
  children,
  neonColor,
  gridOpacity = 0.05,
  variant = "outlined",
  neonGlowIntensity = "medium",
  customBackground,
  cardTitle,
  tooltipInfo,
  sx,
  ...rest
}: NeonCardProps): JSX.Element => {
  const theme = useTheme();
  // Usa a cor primária do tema como padrão se nenhuma cor for especificada
  const defaultNeonColor = alpha(theme.palette.primary.main, 0.3);
  const actualNeonColor = neonColor || defaultNeonColor;
  const actualNeonColorNoOpacity = actualNeonColor.replace(/[\d\.]+\)$/, "1)");

  // Ajusta a intensidade do glow baseado na propriedade neonGlowIntensity
  const getGlowIntensity = () => {
    switch (neonGlowIntensity) {
      case "low":
        return "0 0 10px";
      case "high":
        return "0 0 25px";
      case "medium":
      default:
        return "0 0 15px";
    }
  };

  return (
    <>
      <Paper
        variant={variant}
        sx={{
          p: 2,
          mb: 2,
          position: "relative",
          overflow: "hidden",
          transition: "all 0.3s ease",
          background:
            customBackground ||
            "linear-gradient(135deg, rgba(10,12,16,0.95) 0%, rgba(20,25,35,0.97) 100%)",
          borderColor: actualNeonColor,
          boxShadow: `${getGlowIntensity()} ${actualNeonColor.replace(
            /[\d\.]+\)$/,
            "0.25)"
          )}`,
          borderRadius: "16px",
          backdropFilter: "blur(10px)",
          animation: `${pulseAnimation} 8s infinite ease-in-out`,
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `radial-gradient(circle at 50% 50%, ${actualNeonColor.replace(
              /[\d\.]+\)$/,
              "0.08)"
            )} 0%, rgba(0, 0, 0, 0) 70%)`,
            zIndex: 0,
            pointerEvents: "none",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "1px",
            background: `linear-gradient(90deg, transparent, ${actualNeonColor.replace(
              /[\d\.]+\)$/,
              "0.5)"
            )}, transparent)`,
            zIndex: 1,
            animation: `${glowAnimation} 3s infinite`,
          },
          ...sx,
        }}
        {...rest}
      >
        {/* Grid de fundo estilo Tron */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `linear-gradient(${actualNeonColor.replace(
              /[\d\.]+\)$/,
              `${gridOpacity})`
            )}, transparent 1px), linear-gradient(90deg, ${actualNeonColor.replace(
              /[\d\.]+\)$/,
              `${gridOpacity})`
            )}, transparent 1px)`,
            backgroundSize: "20px 20px",
            backgroundPosition: "center center",
            opacity: 0.5,
            zIndex: 0,
            animation: `${gridMoveAnimation} 25s infinite linear`,
            pointerEvents: "none",
          }}
        />

        {/* Conteúdo do card */}
        <Box position="relative" zIndex={2}>
          {cardTitle && (
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "center",
                color: "#fff",
                textShadow: `0 0 10px ${alpha(actualNeonColor, 0.5)}`,
                fontWeight: "bold",
                marginBottom: "-10px",
              }}
            >
              {tooltipInfo ? (
                <Tooltip title={tooltipInfo} arrow sx={{ mr: .7 }}>
                  <IconButton>
                    <FiInfo style={{ color: actualNeonColorNoOpacity }} />
                  </IconButton>
                </Tooltip>
              ) : (
                <></>
              )}
              {cardTitle}
            </Typography>
          )}
          {children}
        </Box>
      </Paper>
    </>
  );
};

export default NeonCard;
